import { CurrentSeason } from 'common';
import Data from 'data';
import createStore from './factory';

const identity = state => state;

// This is not an actual store
export class ProfileStore {
  static uniqueId = 0;
  static newId() {
    return ++ProfileStore.uniqueId;
  }

  static actions = {
    setName(state, name) {
      if (name === state.name) return state;
      return {...state, name};
    },
    setSkill(state, index, value) {
      const prev = state.skills[index];
      if (typeof value === "function") value = value(prev, state);
      if (value === prev) return state;
      state = {...state, skills: state.skills.slice()};
      state.skills[index] = value;
      return state;
    },
    setPassive(state, index, value) {
      const prev = state.passives[index];
      if (typeof value === "function") value = value(prev, state);
      if (value === prev) return state;
      state = {...state, passives: state.passives.slice()};
      state.passives[index] = value;
      return state;
    },
    setCubeItem(state, type, value) {
      const prev = state.kanai[type];
      if (typeof value === "function") value = value(prev, state);
      if (value === prev) return state;
      return {...state, kanai: {...state.kanai, [type]: value}};
    },
    setParagonLevel(state, level) {
      if (level === state.paragon.level) return state;
      return {...state, paragon: {...state.paragon, level}};
    },
    setParagon(state, value) {
      if (typeof value === "function") value = value(state.paragon, state);
      if (value === state.paragon) return state;
      return {...state, paragon: value};
    },
    setGender(state, gender) {
      if (gender === state.gender) return state;
      return {...state, gender};
    },

    setItem(state, slot, value) {
      const prev = state.items[slot];
      if (typeof value === "function") value = value(prev, state);
      if (value === prev) return state;
      state = {...state, items: {...state.items, [slot]: value}};
      if (slot === "mainhand" && state.class !== "crusader") {
        // unequip offhand logic
        const mh = state.items.mainhand, oh = state.items.offhand;
        if (mh && oh && Data.itemById[mh.id] && Data.itemById[oh.id]) {
          const mhtype = Data.itemById[mh.id].type;
          const ohtype = Data.itemById[oh.id].type;
          if (Data.itemTypes[mhtype] && Data.itemTypes[mhtype].slot === "twohand" && ohtype && ohtype !== "quiver") {
            delete state.items.offhand;
          }
        }
      }
      return state;
    },

    setFollowerItem(state, slot, value) {
      const prev = state.followerItems[slot];
      if (typeof value === "function") value = value(prev, state);
      if (value === prev) return state;
      state = {...state, followerItems: {...state.followerItems, [slot]: value}};
      if (slot === "mainhand") {
        // unequip offhand logic
        const mh = state.followerItems.mainhand, oh = state.followerItems.offhand;
        if (mh && oh && Data.itemById[mh.id] && Data.itemById[oh.id]) {
          const mhtype = Data.itemById[mh.id].type;
          const ohtype = Data.itemById[oh.id].type;
          if (Data.itemTypes[mhtype] && Data.itemTypes[mhtype].slot === "twohand" && ohtype && ohtype !== "quiver") {
            delete state.followerItems.offhand;
          }
        }
      }
      return state;
    },
    setFollowerSkill(state, index, value) {
      const prev = state.followerSkills[index];
      if (typeof value === "function") value = value(prev, state);
      if (value === prev) return state;
      state = {...state, followerSkills: state.followerSkills.slice()};
      state.followerSkills[index] = value;
      return state;
    },

    setActive(state, id, value) {
      const prev = state.active[id];
      if (typeof value === "function") value = value(prev, state);
      if (value === prev) return state;
      return {...state, active: {...state.active, [id]: value}};
    },

    setStatPriority(state, value) {
      const prev = state.statPriority
      if (typeof value === "function") value = value(prev, state);
      if (value === prev) return state;
      return {...state, statPriority: value};
    },

    setProfile(state, value) {
      if (typeof value === "function") value = value(state);
      return value;
    },
  }

  static initialState = {
    id: 0,
    name: "Set 1",
    skills: [...Array(6)],
    passives: [...Array(4)],
    kanai: {},
    paragon: {
      level: 0,
      data: [...Array(4)].map(() => [...Array(4)].map(() => 0)),
    },
    class: "wizard",
    seasonal: CurrentSeason,
    gender: "female",
    items: {},
    active: {},
    statPriority: [
      {stat: "sockets", options: {}, id: 1},
      {stat: "dps", options: {}, id: 2},
      {stat: "toughness", options: {}, id: 3},
    ],
    follower: null,
    followerItems: {},
    followerSkills: [...Array(4)],
  }
}

export default class RootStore extends createStore({
  addProfile(state, profile) {
    if (typeof profile === "function") profile = profile(state);
    if (!profile) return state;
    return {...state, profiles: [...state.profiles, profile], activeProfile: state.profiles.length};
  },

  setProfile(state, value) {
    const prev = state.profiles[state.activeProfile];
    if (typeof value === "function") value = value(prev, state);
    if (value === prev) return state;
    state = {...state, profiles: state.profiles.slice()};
    state.profiles[state.activeProfile] = value;
    return state;
  },

  setActiveProfile(state, id) {
    const index = state.profiles.findIndex(profile => profile.id === id);
    if (index < 0 || index === state.activeProfile) return state;
    return {...state, activeProfile: index};
  },

  removeProfile(state, id) {
    const index = state.profiles.findIndex(profile => profile.id === id);
    if (index < 0 || state.profiles.length <= 1) return state;
    state = {...state};
    state.profiles = state.profiles.slice();
    state.profiles.splice(index, 1);
    if (state.activeProfile > index || state.activeProfile >= state.profiles.length) {
      state.activeProfile -= 1;
    }
    return state;
  },

  removeProfiles(state, ids) {
    if (!state.profiles.some(({id}) => ids[id])) return state;
    state = {...state};
    const active = state.profiles[state.activeProfile];
    state.profiles = state.profiles.filter(profile => !ids[profile.id]);
    if (!state.profiles.length) {
      state.profiles.push({...ProfileStore.initialState, class: active.class, gender: active.gender, seasonal: active.seasonal, id: ProfileStore.newId()});
    }
    state.activeProfile = state.profiles.findIndex(profile => profile.id === active.id);
    if (state.activeProfile < 0) state.activeProfile = 0;
    return state;
  },

  renameProfile(state, id, name) {
    const index = state.profiles.findIndex(profile => profile.id === id);
    if (index < 0 || state.profiles[index].name === name) return state;
    state = {...state};
    state.profiles = state.profiles.slice();
    state.profiles[index] = {...state.profiles[index], name};
    return state;
  },

  moveProfile(state, from, to) {
    if (from === to) return state;
    state = {...state, profiles: state.profiles.slice()};
    const activeId = state.profiles[state.activeProfile].id;
    const src = state.profiles.splice(from, 1);
    state.profiles.splice(to, 0, ...src);
    state.activeProfile = state.profiles.findIndex(profile => profile.id === activeId);
    return state;
  },

  setActive(state, id, value) {
    const prev = state.active[id];
    if (typeof value === "function") value = value(prev, state);
    if (value === prev) return state;
    return {...state, active: {...state.active, [id]: value}};
  },

  setState(state, value) {
    value = {...value};
    if (!value.profiles[value.activeProfile]) {
      value.activeProfile = 0;
    }
    return value;
  },

  setName(state, name) {
    if (name === state.name) return state;
    return {...state, name};
  },
}, {
  profiles: [ProfileStore.initialState],
  activeProfile: 0,
  active: {},
}) {

  constructor() {
    super();
    this.dispatch.profile = {};
    for (const action in ProfileStore.actions) {
      this.dispatch.profile[action] = (...args) => this.dispatch.setProfile(profile => ProfileStore.actions[action](profile, ...args));
    }
  }

  getProfile() {
    const state = this.getState();
    return state.profiles[state.activeProfile];
  }

  setState(value) {
    return new Promise((resolve, reject) => {
      let unsubscribe;
      const handler = () => {
        unsubscribe();
        resolve();
      };
      unsubscribe = this.store.subscribe(handler);
      this.dispatch.setState(value);
    });
  }

  static useProfile(func, depends) {
    func = func || identity;
    return this.useSelector(state => {
      const profile = state.profiles[state.activeProfile];
      return func(profile);
    }, depends);
  }
}

RootStore.instance = new RootStore();
